import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

export default function Analytics() {
  const [dateRange, setDateRange] = useState('30');

  const { data: overview, isLoading: overviewLoading } = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: async () => {
      const response = await api.get('/analytics/overview');
      return response.data;
    }
  });

  const { data: dailyStats } = useQuery({
    queryKey: ['analytics-daily', dateRange],
    queryFn: async () => {
      const response = await api.get('/analytics/daily', { params: { days: dateRange } });
      return response.data;
    },
    select: (data) => {
      // Handle response envelope and extract items array
      const items = data?.data?.items || data?.data || [];
      return Array.isArray(items) ? items : [];
    }
  });

  const { data: topCampaigns } = useQuery({
    queryKey: ['analytics-top-campaigns'],
    queryFn: async () => {
      // Endpoint not implemented in backend yet, return mock data
      return { data: { campaigns: [] } };
    },
    enabled: false, // Disabled until backend endpoint exists
    select: (data) => {
      const campaigns = data?.data?.campaigns || data?.data || [];
      return Array.isArray(campaigns) ? campaigns : [];
    }
  });

  if (overviewLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Analytics</h1>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
          <StatCard
            title="Emails Sent"
            value={overview?.data?.campaigns?.sent || 0}
            change="+12%"
            trend="up"
          />
          <StatCard
            title="Avg Open Rate"
            value={`${(overview?.data?.campaigns?.avg_open_rate || 0).toFixed(1)}%`}
            change="+5%"
            trend="up"
          />
          <StatCard
            title="Avg Click Rate"
            value={`${(overview?.data?.campaigns?.avg_click_rate || 0).toFixed(1)}%`}
            change="+2%"
            trend="up"
          />
          <StatCard
            title="Bounce Rate"
            value={`${(overview?.data?.campaigns?.bounce_rate || 0).toFixed(1)}%`}
            change="-1%"
            trend="down"
            invertTrend={true}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Chart */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Email Activity</h3>
            <SimpleBarChart data={dailyStats || []} />
          </div>

          {/* Top Campaigns */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performing Campaigns</h3>
            <TopCampaigns campaigns={topCampaigns || []} />
          </div>
        </div>

        {/* Engagement Breakdown */}
        <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Breakdown</h3>
          <EngagementBreakdown />
        </div>
      </main>
    </div>
  );
}

function StatCard({ title, value, change, trend, invertTrend = false }: {
  title: string;
  value: string | number;
  change: string;
  trend: 'up' | 'down';
  invertTrend?: boolean;
}) {
  const isPositive = (trend === 'up' && !invertTrend) || (trend === 'down' && invertTrend);
  
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <p className="text-sm font-medium text-gray-600">{title}</p>
      <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
      <div className={`flex items-center mt-2 text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        <span>{change}</span>
        <svg className={`w-4 h-4 ml-1 ${trend === 'up' ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
        <span className="text-gray-500 ml-2">vs last period</span>
      </div>
    </div>
  );
}

function SimpleBarChart({ data }: { data: any[] }) {
  // Defensive check for data
  if (!data || !Array.isArray(data) || data.length === 0) {
    return <div className="text-center text-gray-500 py-8">No data available</div>;
  }

  const maxValue = Math.max(...data.map(d => d.sent || 0), 1);

  return (
    <div className="h-64 flex items-end space-x-1">
      {data.map((day, index) => (
        <div key={index} className="flex-1 flex flex-col items-center">
          <div
            className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
            style={{ height: `${(day.sent / maxValue) * 100}%` }}
            title={`Sent: ${day.sent}`}
          ></div>
          <div className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left truncate w-full text-center">
            {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </div>
        </div>
      ))}
    </div>
  );
}

function TopCampaigns({ campaigns }: { campaigns: any[] }) {
  // Defensive check for campaigns
  if (!campaigns || !Array.isArray(campaigns) || campaigns.length === 0) {
    return <div className="text-center text-gray-500 py-8">No campaigns yet</div>;
  }

  return (
    <div className="space-y-4">
      {campaigns.map((campaign: any, index: number) => (
        <div key={campaign.id} className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">
              {index + 1}
            </div>
            <div>
              <p className="font-medium text-gray-900">{campaign.name}</p>
              <p className="text-sm text-gray-500">{campaign.subject}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-semibold text-gray-900">{campaign.open_rate.toFixed(1)}%</p>
            <p className="text-xs text-gray-500">open rate</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function EngagementBreakdown() {
  const breakdown = [
    { label: 'Opened', percentage: 45, color: 'bg-green-500' },
    { label: 'Clicked', percentage: 25, color: 'bg-blue-500' },
    { label: 'Unsubscribed', percentage: 2, color: 'bg-red-500' },
    { label: 'Bounced', percentage: 3, color: 'bg-orange-500' },
    { label: 'No Activity', percentage: 25, color: 'bg-gray-300' },
  ];

  return (
    <div>
      <div className="flex h-4 rounded-full overflow-hidden bg-gray-200 mb-4">
        {breakdown.map((item) => (
          <div
            key={item.label}
            className={`${item.color} transition-all`}
            style={{ width: `${item.percentage}%` }}
            title={`${item.label}: ${item.percentage}%`}
          ></div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {breakdown.map((item) => (
          <div key={item.label} className="text-center">
            <div className={`w-3 h-3 ${item.color} rounded-full mx-auto mb-1`}></div>
            <p className="text-sm font-medium text-gray-900">{item.label}</p>
            <p className="text-xs text-gray-500">{item.percentage}%</p>
          </div>
        ))}
      </div>
    </div>
  );
}
